import type { AttachedDoc, ConversationMessage } from '../shared/types';
import { state } from '../shared/state';
import { registerActiveSessionProvider } from '../shared/app-hooks';

export type GenerationStatus = 'idle' | 'streaming' | 'error';

export interface SessionGeneration {
  status: GenerationStatus;
  abortCtrl: AbortController | null;
  partialAssistantText: string;
  categoryName: string;
  startedAt: number;
  serverJobId?: string | null;
  pollTimer?: ReturnType<typeof setInterval> | null;
}

export interface SessionRuntime {
  id: string;
  title: string;
  category: string;
  history: ConversationMessage[];
  attachedDocs: AttachedDoc[];
  draftInput: string;
  generation: SessionGeneration;
  dirty: boolean;
  loaded: boolean;
}

function createGenerationState(): SessionGeneration {
  return {
    status: 'idle',
    abortCtrl: null,
    partialAssistantText: '',
    categoryName: '',
    startedAt: 0,
  };
}

export function createSessionRuntime(id: string, partial: Partial<SessionRuntime> = {}): SessionRuntime {
  return {
    id,
    title: partial.title || '',
    category: partial.category || '',
    history: partial.history ? [...partial.history] : [],
    attachedDocs: partial.attachedDocs ? [...partial.attachedDocs] : [],
    draftInput: partial.draftInput || '',
    generation: partial.generation || createGenerationState(),
    dirty: partial.dirty ?? false,
    loaded: partial.loaded ?? false,
  };
}

export function ensureMessageId(message: ConversationMessage): ConversationMessage {
  if (!message.id) {
    message.id = crypto.randomUUID();
  }
  return message;
}

export function normalizeMessages(messages: ConversationMessage[]): ConversationMessage[] {
  return messages.map((m) => ensureMessageId({ ...m }));
}

class SessionStoreImpl {
  private sessions = new Map<string, SessionRuntime>();
  private serverGeneratingSessions = new Set<string>();
  activeSessionId: string | null = null;
  maxParallelGenerations = 3;

  setServerGeneratingSessions(ids: Iterable<string>): void {
    this.serverGeneratingSessions = new Set(ids);
  }

  removeServerGeneratingSession(sessionId: string): void {
    this.serverGeneratingSessions.delete(sessionId);
  }

  /** Ignore stale `generating` from /api/sessions when local session already finished. */
  shouldTrustServerGenerating(sessionId: string): boolean {
    const local = this.getSession(sessionId);
    if (!local) return true;
    return local.generation.status === 'streaming' || !!local.generation.serverJobId;
  }

  isServerGenerating(sessionId: string): boolean {
    return this.serverGeneratingSessions.has(sessionId);
  }

  isSessionGenerating(sessionId: string): boolean {
    const session = this.getSession(sessionId);
    return session?.generation.status === 'streaming' || this.serverGeneratingSessions.has(sessionId);
  }

  getSession(id: string | null | undefined): SessionRuntime | null {
    if (!id) return null;
    return this.sessions.get(id) ?? null;
  }

  getOrCreate(id: string, defaults: Partial<SessionRuntime> = {}): SessionRuntime {
    let session = this.sessions.get(id);
    if (!session) {
      session = createSessionRuntime(id, defaults);
      this.sessions.set(id, session);
    }
    return session;
  }

  getActiveSession(): SessionRuntime | null {
    return this.getSession(this.activeSessionId);
  }

  setActiveSession(id: string | null): SessionRuntime | null {
    this.activeSessionId = id;
    return id ? this.getOrCreate(id) : null;
  }

  deleteSession(id: string): void {
    this.sessions.delete(id);
    if (this.activeSessionId === id) {
      this.activeSessionId = null;
    }
  }

  clear(): void {
    this.sessions.clear();
    this.activeSessionId = null;
  }

  listSessions(): SessionRuntime[] {
    return [...this.sessions.values()];
  }

  isGenerating(sessionId?: string | null): boolean {
    if (sessionId) {
      return this.getSession(sessionId)?.generation.status === 'streaming';
    }
    return this.listSessions().some((s) => s.generation.status === 'streaming');
  }

  streamingCount(): number {
    const local = this.listSessions().filter((s) => s.generation.status === 'streaming').length;
    const serverOnly = [...this.serverGeneratingSessions].filter(
      (id) => this.getSession(id)?.generation.status !== 'streaming',
    ).length;
    return local + serverOnly;
  }

  hasIndexingAttachments(sessionId?: string | null): boolean {
    const check = (docs: AttachedDoc[]) =>
      docs.some((d) => d.id && (d.status === 'pending' || d.status === 'processing'));

    if (sessionId) {
      const session = this.getSession(sessionId);
      return session ? check(session.attachedDocs) : false;
    }
    return this.listSessions().some((s) => check(s.attachedDocs));
  }

  captureDraftFromUI(): void {
    const active = this.getActiveSession();
    if (!active) return;
    const input = document.getElementById('user-input') as HTMLTextAreaElement | null;
    if (input) {
      active.draftInput = input.value;
    }
  }

  applyDraftToUI(): void {
    const active = this.getActiveSession();
    const input = document.getElementById('user-input') as HTMLTextAreaElement | null;
    if (!input || !active) return;
    input.value = active.draftInput || '';
  }
}

export const sessionStore = new SessionStoreImpl();

export function hydrateSessionFromServer(
  id: string,
  data: { title?: string; category?: string; messages?: ConversationMessage[] },
): SessionRuntime {
  const session = sessionStore.getOrCreate(id, {
    title: data.title || '',
    category: data.category || state.currentUser?.category || '',
    history: normalizeMessages((data.messages || []).filter((m) => m.role !== 'system')),
    loaded: true,
  });
  session.title = data.title || session.title;
  session.category = data.category || session.category;
  session.history = normalizeMessages((data.messages || []).filter((m) => m.role !== 'system'));
  session.loaded = true;
  return session;
}

// shared/ui and shared/system-prompt reach the active session through this
// hook instead of importing the store (ADR-002 phase C inversion).
registerActiveSessionProvider(() => sessionStore.getActiveSession());
